import React from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Table, Tag, Tooltip, Progress } from 'antd';
import { useTranslation } from 'react-i18next';
import { fmtUtc, fmtDate, fmtDuration, fmtCount } from './format';

// Latest business day in each table the reports read.
//
// Days behind is measured against the newest FACT date, not today: the facts are
// what the nightly loads, so an aggregate trailing them is an aggregate whose
// load did not run -- which is how six of them sat frozen at 2026-08-27 for a
// fortnight without anything failing.
export function TableFreshness({ tables }) {
  const { t } = useTranslation();
  const rows = tables ?? [];
  const newestFact = _.max(rows.filter((r) => r.kind === 'FACT').map((r) => r.latestBusinessDate));

  return (
    <div className="rounded-card border border-line bg-surface p-4 shadow-card">
      <div className="mb-1 text-sm font-semibold text-ink">{t('dw_tables_title')}</div>
      <p className="mb-3 text-xs leading-relaxed text-muted">{t('dw_tables_desc')}</p>
      <Table
        size="small"
        rowKey="tableName"
        pagination={false}
        scroll={{ x: 'max-content' }}
        dataSource={rows}
        locale={{ emptyText: t('no_data') }}
        columns={[
          {
            title: t('dw_table'),
            key: 'name',
            render: (r) => (
              <span className="whitespace-nowrap">
                <span className="font-mono text-xs text-ink">{r.tableName}</span>
                {r.feedsAnalyticsPages && (
                  <Tooltip title={t('dw_feeds_pages_help')}>
                    <Tag color="gold" className="ml-2 cursor-help">{t('dw_feeds_pages')}</Tag>
                  </Tooltip>
                )}
                {r.isWeekly && <Tag className="ml-2">{t('dw_weekly')}</Tag>}
              </span>
            ),
          },
          {
            title: t('dw_data_through'),
            key: 'through',
            render: (r) => <span className="whitespace-nowrap tabular-nums">{r.latestBusinessDate ? fmtDate(r.latestBusinessDate) : t('dw_empty')}</span>,
          },
          {
            title: t('dw_behind_facts'),
            key: 'behind',
            align: 'right',
            render: (r) => {
              if (r.kind === 'FACT' || r.isWeekly || !newestFact) return <span className="text-muted">—</span>;
              if (!r.latestBusinessDate) return <Tag color="error">{t('dw_empty')}</Tag>;
              const days = dayjs(newestFact).diff(dayjs(r.latestBusinessDate), 'day');
              return days > 0
                ? <Tag color={days > 1 ? 'error' : 'warning'}>{t('dw_days_behind', { count: days })}</Tag>
                : <Tag color="success">{t('dw_current')}</Tag>;
            },
          },
        ]}
      />
    </div>
  );
}

// The registered transform chain in execution order.
//
// PhaseOnly is the column that matters: a REPORTING or WEEKLY step is registered
// but NOT part of the nightly, so its tables only move when someone runs it.
export function TransformChain({ steps }) {
  const { t } = useTranslation();

  return (
    <div className="rounded-card border border-line bg-surface p-4 shadow-card">
      <div className="mb-1 text-sm font-semibold text-ink">{t('dw_chain_title')}</div>
      <p className="mb-3 text-xs leading-relaxed text-muted">{t('dw_chain_desc')}</p>
      <Table
        size="small"
        rowKey="procedureName"
        pagination={false}
        scroll={{ x: 'max-content' }}
        dataSource={steps ?? []}
        locale={{ emptyText: t('no_data') }}
        columns={[
          {
            title: '#',
            key: 'order',
            render: (s) => <span className="tabular-nums text-xs text-muted">{s.stepGroup}.{s.stepSequence}</span>,
          },
          {
            title: t('dw_procedure'),
            key: 'proc',
            render: (s) => (
              <span className={`whitespace-nowrap font-mono text-xs ${s.isEnabled ? 'text-ink' : 'text-muted line-through'}`}>
                {s.procedureName}
              </span>
            ),
          },
          {
            title: t('dw_runs_in'),
            key: 'phase',
            render: (s) => {
              if (!s.isEnabled) return <Tag>{t('dw_disabled')}</Tag>;
              return s.phaseOnly
                ? <Tooltip title={t('dw_phase_only_help')}><Tag color="warning" className="cursor-help">{s.phaseOnly}</Tag></Tooltip>
                : <Tag color="success">{t('dw_nightly')}</Tag>;
            },
          },
          {
            title: `${t('dw_last_success')} (UTC)`,
            key: 'last',
            render: (s) => (s.lastSucceededRunId == null
              ? <Tooltip title={t('dw_never_recorded_help')}><span className="cursor-help text-xs text-muted">{t('dw_never_recorded')}</span></Tooltip>
              : (
                <Tooltip title={s.lastSucceededRunType}>
                  <span className="cursor-help whitespace-nowrap text-xs tabular-nums">
                    {fmtUtc(s.lastSucceededAtUtc)} <span className="text-muted">· {t('dw_run')} {s.lastSucceededRunId}</span>
                  </span>
                </Tooltip>
              )),
          },
          {
            title: t('dw_duration'),
            key: 'duration',
            align: 'right',
            render: (s) => <span className="tabular-nums text-xs">{fmtDuration(s.lastDurationSeconds)}</span>,
          },
        ]}
      />
    </div>
  );
}

// Historical backfill progress, one row per source table (etl.vBackfillProgress).
export function BackfillProgress({ backfill }) {
  const { t } = useTranslation();

  return (
    <div className="rounded-card border border-line bg-surface p-4 shadow-card">
      <div className="mb-1 text-sm font-semibold text-ink">{t('dw_backfill_title')}</div>
      <p className="mb-3 text-xs leading-relaxed text-muted">{t('dw_backfill_desc')}</p>
      <Table
        size="small"
        rowKey="tableKey"
        pagination={false}
        scroll={{ x: 'max-content' }}
        dataSource={backfill ?? []}
        locale={{ emptyText: t('no_data') }}
        columns={[
          { title: t('dw_source_table'), key: 'table', render: (b) => <span className="whitespace-nowrap font-mono text-xs text-ink">{b.tableKey}</span> },
          {
            title: t('dw_progress'),
            key: 'progress',
            render: (b) => (
              <div className="min-w-48">
                <Progress
                  size="small"
                  percent={Number(b.pctComplete ?? 0)}
                  status={b.chunksFailed > 0 ? 'exception' : undefined}
                />
              </div>
            ),
          },
          {
            title: t('dw_chunks'),
            key: 'chunks',
            align: 'right',
            render: (b) => (
              <span className="whitespace-nowrap tabular-nums text-xs">
                {b.chunksComplete}/{b.chunksTotal}
                {b.chunksPending > 0 && <span className="text-muted"> · {t('dw_pending_count', { count: b.chunksPending })}</span>}
                {b.chunksFailed > 0 && <span className="text-danger"> · {t('dw_failed_count', { count: b.chunksFailed })}</span>}
              </span>
            ),
          },
          { title: t('dw_rows_copied'), key: 'rows', align: 'right', render: (b) => <span className="tabular-nums text-xs">{fmtCount(b.rowsCopied)}</span> },
          { title: t('dw_avg_chunk'), key: 'avg', align: 'right', render: (b) => <span className="tabular-nums text-xs">{fmtDuration(b.avgChunkSeconds)}</span> },
          { title: `${t('dw_last_activity')} (UTC)`, key: 'last', render: (b) => <span className="whitespace-nowrap tabular-nums text-xs">{fmtUtc(b.lastActivityUtc)}</span> },
        ]}
      />
    </div>
  );
}
